import { yupResolver } from "@hookform/resolvers/yup";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Style from "@mui/icons-material/Style";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useSetAtom } from "jotai";
import { countBy } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "react-hot-toast";
import { createSearchParams, useNavigate, useSearchParams } from "react-router";
import { useToken } from "../../server/auth/use-token";
import { useFlag } from "../../server/optimizely";
import { AutoAwesomeColouredOutlined } from "../../styles/icons/auto-awesome-coloured-outlined";
import { EMPTY_SX } from "../../utils/empty-sx";
import { useDialog } from "../../utils/hooks/use-dialog";
import yup from "../../utils/yup";
import { usePendo } from "../analytics/hooks/use-pendo-watch";
import { trackContentGeneration } from "../content-generation/track-content-generation";
import { useAccountLimitsContext } from "../context/account-limit-context";
import { GradientBackground } from "../generic/components/gradient-background";
import { PiiGuidanceNotice } from "../generic/components/pii-guidance-notice";
import { useMultiformat } from "../multiformat/hooks/use-multi-format";
import { contentGenerationIdAtom } from "../multiformat/store/content-generation-id-atom";
import { isLongform } from "../multiformat/store/types";
import { ContentDescriptionTextField } from "./content-description-textfield";
import { ContentToneSelect } from "./content-tone-select";
import { ContentTypeSelect, SURVEY_FORMAT } from "./content-type-select";
import { CreateContentLinks } from "./create-content-links";
import { generateSurvey } from "./generate-survey";
import { useBrandToneOptions } from "./hooks/use-brand-tone-options";
import { ImageStyleSelector } from "./image-style-selector";
import { InspireMeButton } from "./inspire-me-button";
import { LongformDialog } from "./longform-prompt-dialog";
import { mapLongformParams } from "./map-longform-params";
import { PhotoTypeSelect } from "./photo-type-select";

import type { CreateContentRequest } from "../multiformat/store/types";
import type { LongFormParams } from "./map-longform-params";
import type { BoxProps } from "@mui/material";
import type { FC } from "react";

const customToneEnabled = (
  multiformatType: string | undefined,
  queryFormat: string | undefined,
): boolean => {
  const noCustomToneTypes = new Set([
    "Landscape Logo",
    "Square Logo",
    "TV Display",
    "Certificate",
    "Video Call Background",
    "Horizontal Banner",
    "Meme",
  ]);

  return !noCustomToneTypes.has(multiformatType ?? "") && !noCustomToneTypes.has(queryFormat ?? "");
};

export const CreateContentCard: FC<{ onClose?: () => void } & BoxProps> = ({
  onClose,
  sx = EMPTY_SX,
  ...props
}) => (
  <GradientBackground sx={[...(Array.isArray(sx) ? sx : [sx])]} {...props}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
        p: 2,
      }}
    >
      <CreateContentLinks />
    </Box>
    <Box sx={{ px: 2, pb: 8 }}>
      {onClose && (
        <IconButton
          data-analytics-id="create-content-close"
          onClick={onClose}
          sx={(theme) => ({
            position: "relative",
            float: "right",
            top: theme.spacing(-3),
            right: theme.spacing(-1),
            [theme.breakpoints.down("md")]: {
              right: theme.spacing(2),
            },
          })}
        >
          <Close data-testid="CloseIcon" />
        </IconButton>
      )}
      <Box
        sx={{
          width: "100%",
          maxWidth: "600px",
          mx: "auto",
          boxSizing: "border-box",
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 4 }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <AutoAwesomeColouredOutlined />
            <Typography variant="h5" sx={{ color: "text.primary" }}>
              Generate with AI
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Button
              href="/moments"
              data-analytics-id="create-content-from-scratch"
              size="small"
              variant="text"
              endIcon={<ChevronRight />}
            >
              Communication from Scratch
            </Button>
            <Button
              href="/moments/newsletter"
              data-analytics-id="create-content-newsletter"
              size="small"
              variant="text"
              endIcon={<ChevronRight />}
            >
              Newsletter
            </Button>
          </Stack>
        </Stack>
        <CreateContentForm onClose={onClose} />
      </Box>
    </Box>
  </GradientBackground>
);

export const CreateContentForm: FC<{
  id?: string;
  onClose?: () => void;
  includeSurvey?: boolean;
  hideSubmit?: boolean;
  onSubmittingChange?: (submitting: boolean) => void;
}> = ({ id, onClose, includeSurvey, hideSubmit, onSubmittingChange }) => {
  const multiformatLoader = useMultiformat();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandToneOptions = useBrandToneOptions();
  const pendo = usePendo();
  const { enable_pii_guidance } = useAccountLimitsContext();
  const [prototypes, , prototypesLoaded] = useFlag("mfai_prototypes");
  const getToken = useToken();
  const setContentGenerationId = useSetAtom(contentGenerationIdAtom);

  const schema = useMemo(
    () =>
      yup.object({
        // A survey can start blank, so the prompt is optional for it
        q: yup
          .string()
          .defined()
          .when("format", ([format], qSchema) =>
            format !== SURVEY_FORMAT
              ? qSchema.required("Please describe the content you wish to create.")
              : qSchema,
          ),
        tone: yup
          .string()
          .oneOf(brandToneOptions.data?.map((option) => option.value) ?? [])
          .optional(),
        format: yup.string().optional(),
        image_style: yup.object({
          photo: yup.boolean().required(),
          illustration: yup.boolean().required(),
          icon: yup.boolean().required(),
          video: yup.boolean().required(),
        }),
        db: yup.string().optional(),
        file: yup
          .object({
            id: yup.string().optional(),
            name: yup.string().optional(),
          })
          .optional()
          .default(undefined),
      }),
    [brandToneOptions],
  );

  const methods = useForm({
    defaultValues: {
      q: searchParams.get("q") ?? searchParams.get("answer") ?? "",
      format: searchParams.get("format") ?? "Communication",
      tone: searchParams.get("tone") ?? undefined,
      image_style: searchParams.get("image_styles")
        ? {
            photo: searchParams.get("image_styles")?.includes("photo") ?? true,
            illustration: searchParams.get("image_styles")?.includes("illustration") ?? true,
            icon: searchParams.get("image_styles")?.includes("icon") ?? true,
            video: searchParams.get("image_styles")?.includes("video") ?? true,
          }
        : {
            photo: true,
            illustration: true,
            icon: true,
            video: true,
          },
      db: searchParams.get("db") ?? undefined,
      file: searchParams.get("file_id")
        ? {
            id: searchParams.get("file_id") ?? undefined,
            name: searchParams.get("file_name") ?? undefined,
          }
        : undefined,
    },
    resolver: yupResolver(schema),
  });

  const [format, q, file] = useWatch({ control: methods.control, name: ["format", "q", "file"] });
  // A survey only needs a prompt, so the other options are hidden
  const isSurvey = format === SURVEY_FORMAT;
  // Generating a survey takes a few seconds before we can redirect to it
  const { isSubmitting } = methods.formState;
  const creatingSurvey = isSurvey && isSubmitting;

  // Lets a submit button outside the form, like the dashboard dialog's, disable itself
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  // Closing the dialog unmounts the form, which cancels a survey that's still generating
  const surveyAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => surveyAbortRef.current?.abort(), []);

  const longformDialog = useDialog<LongFormParams>();

  const handleSubmit = useCallback(
    async (request: CreateContentRequest) => {
      if (!multiformatLoader.data || !format) return;
      if (format === SURVEY_FORMAT) {
        if (!request.q) {
          void navigate("/listen/survey/new");
          onClose?.();
          return;
        }
        const abortController = new AbortController();
        surveyAbortRef.current = abortController;
        try {
          const search = await generateSurvey(await getToken(), request.q, abortController.signal);
          if (abortController.signal.aborted) return;
          void navigate({ pathname: "/listen/survey/new", search });
          onClose?.();
        } catch (error) {
          if (abortController.signal.aborted) return;
          console.error(error);
          toast.error("Failed to generate survey");
        }
        return;
      }

      const config = multiformatLoader.data.content_types.find((c) => c.name === format);
      const imageStyles =
        Object.entries(request?.image_style ?? {})
          .filter(([, value]) => value)
          .map(([key]) => key)
          .join(",") || null;

      const sharedParams = {
        format,
        ...(request.file && { file_id: request.file.id, file_name: request.file.name }),
        ...(imageStyles && { image_styles: imageStyles }),
        ...(request.db && { db: request.db }),
        ...(request.tone && { tone: request.tone }),
      };

      const token = await getToken();
      const toneName = brandToneOptions.data?.find((t) => t.value === request.tone)?.label ?? null;
      if (config?.type === "Communication") {
        pendo?.track("create-content-prompt", {
          prompt: request.q,
          ...sharedParams,
        });
        const pathname = "/generate-comms";
        const params = createSearchParams({
          q: request.q,
          ...sharedParams,
        });

        const contentGenerationId = await trackContentGeneration(token, {
          contentType: "moment",
          prompt: JSON.stringify({ prompt: request.q, tone: toneName }),
        });
        setContentGenerationId(contentGenerationId);
        void navigate({ pathname, search: params.toString() });
        return;
      }

      if (isLongform(format) && file === undefined && request.q.length < 500) {
        pendo?.track("create-content-prompt-too-short", {
          prompt: request.q,
          ...sharedParams,
        });
        longformDialog.handleOpen(sharedParams);
        return;
      }

      pendo?.track("create-content-prompt", {
        prompt: request.q,
        ...sharedParams,
      });
      const params = createSearchParams({
        answer: request.q,
        ...sharedParams,
      });

      const contentGenerationId = await trackContentGeneration(token, {
        contentType: "mfai",
        subType: format,
        prompt: JSON.stringify({ prompt: request.q, tone: toneName }),
      });
      setContentGenerationId(contentGenerationId);
      void navigate({ pathname: "/create-content/preview", search: params.toString() });

      onClose?.();
    },
    [
      multiformatLoader.data,
      format,
      getToken,
      file,
      pendo,
      navigate,
      onClose,
      longformDialog,
      setContentGenerationId,
      brandToneOptions.data,
    ],
  );

  const handleInspire = useCallback(
    (prompt: string) => {
      methods.setValue("q", prompt, { shouldDirty: true, shouldTouch: true });
    },
    [methods],
  );

  const handleSkip = useCallback(
    async (longFormParams?: LongFormParams) => {
      const params = createSearchParams({
        answer: q,
        format: format ?? "",
        ...mapLongformParams(longFormParams ?? {}),
      });
      const token = await getToken();
      const toneName =
        brandToneOptions.data?.find((t) => t.value === methods.getValues("tone"))?.label ?? null;
      const contentGenerationId = await trackContentGeneration(token, {
        contentType: "mfai",
        subType: format ?? "",
        prompt: JSON.stringify({ prompt: q, tone: toneName }),
      });
      setContentGenerationId(contentGenerationId);
      void navigate({ pathname: "/create-content/preview", search: params.toString() });
      longformDialog.handleClose();
    },
    [
      q,
      format,
      navigate,
      longformDialog,
      setContentGenerationId,
      getToken,
      brandToneOptions.data,
      methods,
    ],
  );

  const handleContinue = useCallback(
    async (prompt: string, longFormParams?: LongFormParams) => {
      const params = createSearchParams({
        answer: prompt,
        format: format ?? "",
        ...mapLongformParams(longFormParams ?? {}),
      });
      const token = await getToken();
      const toneName =
        brandToneOptions.data?.find((t) => t.value === methods.getValues("tone"))?.label ?? null;
      const contentGenerationId = await trackContentGeneration(token, {
        contentType: "mfai",
        subType: format ?? "",
        prompt: JSON.stringify({ prompt, tone: toneName }),
      });
      setContentGenerationId(contentGenerationId);
      void navigate({ pathname: "/create-content/preview", search: params.toString() });
      longformDialog.handleClose();
    },
    [
      format,
      navigate,
      longformDialog,
      setContentGenerationId,
      getToken,
      brandToneOptions.data,
      methods,
    ],
  );

  const imageStyleConfig = useMemo(() => {
    if (multiformatLoader.loading) return undefined;
    return countBy(
      multiformatLoader.data?.content_types
        .find((c) => c.name === format)
        ?.layouts?.map((l) => l.images[0]?.type),
    );
  }, [multiformatLoader.data, multiformatLoader.loading, format]);

  return (
    <FormProvider {...methods}>
      <form id={id} onSubmit={(e) => void methods.handleSubmit(handleSubmit)(e)}>
        <Box sx={{ position: "relative" }}>
          {creatingSurvey && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Typography>Creating your survey…</Typography>
              <LinearProgress variant="indeterminate" sx={{ width: "100%", maxWidth: 200 }} />
            </Box>
          )}
          {/* Hidden rather than unmounted so the dialog keeps its size while the survey generates */}
          <Box sx={{ visibility: creatingSurvey ? "hidden" : "visible" }}>
            <Box sx={{ mb: 2 }}>
              <ContentTypeSelect
                prototypes={prototypes}
                loaded={prototypesLoaded}
                includeSurvey={includeSurvey}
              />
            </Box>
            {!isSurvey && (
              <Box sx={{ mb: 3 }}>
                {!multiformatLoader.data && <Skeleton height="60px" variant="rounded" />}
                {multiformatLoader.data &&
                  customToneEnabled(
                    multiformatLoader.data.content_types.find((c) => c.name === format)?.type,
                    format,
                  ) && <ContentToneSelect />}
              </Box>
            )}
            <Stack sx={{ gap: 0.5 }}>
              {!isSurvey && (
                <Box
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <Box>
                    {enable_pii_guidance && <PiiGuidanceNotice analyticsId="create-content" />}
                  </Box>
                  <Box>
                    <InspireMeButton onInspire={handleInspire} format={format ?? ""} />
                  </Box>
                </Box>
              )}
              <ContentDescriptionTextField
                format={format}
                hideSubmit={hideSubmit}
                fileUpload={Boolean(
                  format &&
                  ![
                    "Square Logo",
                    "Landscape Logo",
                    "TV Display",
                    "Video Call Background",
                    "Certificate",
                    "Horizontal Banner",
                    "Meme",
                    "Social Post",
                    SURVEY_FORMAT,
                  ].includes(format),
                )}
              />
              {isLongform(format) && (
                <Stack direction="row" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <InfoOutlined fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    Consider entering around 1,000 characters for the best results
                  </Typography>
                </Stack>
              )}
            </Stack>
            {!isSurvey && <AdvancedOptions imageStyleConfig={imageStyleConfig} />}
          </Box>
        </Box>
        {longformDialog.open && (
          <LongformDialog
            open={longformDialog.open}
            contentType={format ?? ""}
            prompt={q}
            onSkip={() => void handleSkip()}
            onContinue={(prompt) => void handleContinue(prompt, longformDialog.data)}
            onClose={longformDialog.handleClose}
          />
        )}
      </form>
    </FormProvider>
  );
};

const AdvancedOptions: FC<{ imageStyleConfig: Record<string, number> | undefined }> = ({
  imageStyleConfig,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        onClick={() => setOpen((prev) => !prev)}
        data-analytics-id="create-content-advanced-options"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          color: "primary.main",
          userSelect: "none",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderRadius: 2,
            transition: "background 0.2s ease",
            "&:hover": { backgroundColor: "rgba(72,83,255,0.05)" },
          }}
        >
          <Style fontSize="small" color="primary" />
          Advanced style options
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Typography>
      </Box>
      {open && (
        <>
          {imageStyleConfig && <ImageStyleSelector config={imageStyleConfig} />}
          <PhotoTypeSelect />
        </>
      )}
    </Box>
  );
};
